import { useState } from 'react';
import { Group, Text, Badge, Table, Select } from '@mantine/core';
import { IconAlertTriangle, IconGift } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { TableToolbar } from '../components/TableToolbar';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { SILENT_ERROR } from '../lib/queryMeta';
import { FetchOverlay } from '../components/FetchOverlay';
import { EmptyState } from '../components/EmptyState';
import { FeatureFrozen } from '../components/FeatureFrozen';
import { SectionCard } from '../components/SectionCard';
import { formatDate } from '../lib/format';
import { PREMIUM_UI_ENABLED } from '../lib/features';

interface GiftRow {
  id: string; months: number; message?: string | null; anonymous: boolean; createdAt: string;
  from: { fullName: string; phone?: string | null };
  to: { fullName: string; phone?: string | null };
}

/**
 * Marshrut komponenti (`/premium`).
 *
 * Premium muzlatilganda (`lib/features.ts` → `PREMIUM_UI_ENABLED = false`) bu band
 * yon menyuda yo'q, lekin URL ochiq — shuning uchun sahifa o'rniga "vaqtincha
 * o'chirilgan" holati chiziladi. Tekshiruv ATAYLAB hook'lardan OLDIN: muzlatilgan
 * paytda ortiqcha so'rov ham ketmasin (asosiy mantiq `PremiumGifts` da).
 */
export default function Premium() {
  if (!PREMIUM_UI_ENABLED) return <FeatureFrozen titleKey="premiumPage.title" />;
  return <PremiumGifts />;
}

/** Premium sovg'alar tarixi — kim kimga yuborgani (anonim bo'lsa ham admin ko'radi).
 *  Obuna boshqaruvi Ota-onalar sahifasida. */
function PremiumGifts() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [anonF, setAnonF] = useState<string>('');
  const [sort, setSort] = useState('createdAt:desc');

  // Xato ro'yxat o'rnida ochiq ko'rsatiladi (EmptyState) — global toast takrorlamasin
  /* Filtrlar BITTA joyda: jadval ham, eksport ham AYNI obyektni oladi. */
  const filtrlar = {
    q: q || undefined,
    anonymous: anonF || undefined,
    sort,
  };
  const gifts = usePagedQuery<GiftRow>('premium-gifts', '/premium/gifts', filtrlar, 20, true, SILENT_ERROR);

  return (
    <>
      <PageHeader title={t('premiumPage.title')} subtitle={`${t('common.total')}: ${gifts.total} — ${t('premiumPage.subManage')}`} />
      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); gifts.setPage(1); }}
        searchPlaceholder={t('premiumPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); gifts.setPage(1); }}
        sortOptions={[
          { value: 'createdAt:desc', label: t('att.sortDateDesc') },
          { value: 'createdAt:asc', label: t('att.sortDateAsc') },
          { value: 'months:desc', label: t('premiumPage.sortMonthsDesc') },
        ]}
      >
        <Select
          placeholder={t('premiumPage.anonymity')}
          data={[{ value: 'yes', label: t('premiumPage.anonymous') }, { value: 'no', label: t('premiumPage.open') }]}
          value={anonF}
          onChange={(v) => { setAnonF(v ?? ''); gifts.setPage(1); }}
          clearable w={130}
        />
        <TableExport<GiftRow>
          endpoint="/premium/gifts"
          params={filtrlar}
          filename={t('nav.premium')}
          ustunlar={[
            { key: 'from', label: t('premiumPage.from'), get: (r) => r.from.fullName },
            { key: 'to', label: t('premiumPage.to'), get: (r) => r.to.fullName },
            { key: 'months', label: t('parentDetail.monthsGive') },
            { key: 'createdAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.createdAt}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconGift} p={0} pos="relative">
        <FetchOverlay visible={gifts.fetching} />
        {gifts.isError ? (
          <EmptyState icon={IconAlertTriangle} title={t('premiumPage.errorTitle')} description={t('premiumPage.errorDesc')} />
        ) : !gifts.isLoading && !gifts.items.length ? (
          <EmptyState icon={IconGift} title={t('premiumPage.emptyTitle')} description={t('premiumPage.emptyDesc')} />
        ) : (
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('premiumPage.from')}</Table.Th>
                  <Table.Th>{t('premiumPage.to')}</Table.Th>
                  <Table.Th>{t('premiumPage.duration')}</Table.Th>
                  <Table.Th>{t('premiumPage.greeting')}</Table.Th>
                  <Table.Th>{t('fields.date')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {gifts.items.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td fw={500}>
                      <Group gap={6}>
                        {g.from.fullName}
                        {g.anonymous && <Badge size="xs" color="gray">{t('premiumPage.sentAnon')}</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed">{g.from.phone}</Text>
                    </Table.Td>
                    <Table.Td fw={500}>
                      {g.to.fullName}
                      <Text size="xs" c="dimmed">{g.to.phone}</Text>
                    </Table.Td>
                    <Table.Td><Badge color="yellow" variant="light">{t('premiumPage.months', { n: g.months })}</Badge></Table.Td>
                    <Table.Td maw={280}><Text size="sm" truncate>{g.message ?? '—'}</Text></Table.Td>
                    <Table.Td>{formatDate(g.createdAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={gifts.page}
        pages={gifts.pages}
        total={gifts.total}
        limit={gifts.limit}
        onPage={gifts.setPage}
        onLimit={gifts.setLimit}
      />
    </>
  );
}
